import React, { useEffect, useMemo, useState } from "react";
import useAuth from "../hooks/useAuth";
import { usePortalUi } from "../contexts/PortalUiContext";

import TimeFilter from "./TimeFilter";
import { schedulerApiFetch } from "../api/schedulerClient";
import { useQuery } from "@tanstack/react-query";
import { useUserConfig, getTimezoneFromUserConfig } from "../queries/useUserConfig";

function HeaderClock() {
  const { user } = useAuth();
  const [time, setTime] = useState(new Date());
  const { data: userConfigData } = useUserConfig({ enabled: Boolean(user) });
  const tzFromUser = getTimezoneFromUserConfig(userConfigData?.config);

  const { data: fallbackConfig } = useQuery({
    queryKey: ["schedulerConfig", "fallbackTimezone"],
    queryFn: async () => {
      const response = await schedulerApiFetch("config", {
        minimalHeaders: true,
      });
      if (!response.ok) return null;
      return response.json();
    },
    enabled: Boolean(user) && !tzFromUser,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const timezone =
    tzFromUser ||
    fallbackConfig?.la_timezone ||
    fallbackConfig?.timeZone ||
    "America/Los_Angeles";

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatted = useMemo(() => {
    const datePart = time.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone,
    });
    const timePart = time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: timezone,
    });
    return `${datePart} - ${timePart}`;
  }, [time, timezone]);

  // UI: keep exactly the old markup/classes.
  return (
    <div className="flex items-center justify-center gap-2 rounded-full px-3 py-2 bg-main-primary/5 w-48 shrink-0 ring-1 ring-header-primary/10">
      <div className="hidden sm:block">
        <svg
          className="w-5 h-5 text-[#505665]"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="text-[10px] sm:text-xs font-bold text-main-primary/80 sm:tracking-[2px] whitespace-nowrap">
        {formatted}
      </div>
    </div>
  );
}

// ... unchanged parts omitted ...

export default function Header({ companyName }) {
  const { setSidebarOpen, setLogoutModalOpen } = useAuth();
  const { isShowSidenav } = usePortalUi();

  const handleLogoutModal = () => {
    setLogoutModalOpen(true);
  };

  return (
    <header className="w-full border-b shadow bg-header-base border-gray-200">
      <div className="px-2">
        <div className="relative flex h-14 justify-between">
          {/* Mobile menu toggle — hidden when portal hides sidenav */}
          {isShowSidenav && (
            <div className="flex items-center lg:hidden">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="inline-flex items-center justify-center rounded-md p-2 text-header-light hover:bg-header-hover-btn hover:text-header-hover-light focus:outline-none focus:ring-2 focus:ring-inset focus:ring-header-primary"
                aria-controls="mobile-menu"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className="block h-6 w-6"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          <div className="flex flex-1 items-center gap-6 pr-2 lg:pl-2">
            <div className="flex-1 flex items-center">
              <TimeFilter headerClock={isShowSidenav ? <HeaderClock /> : null} />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
